/**
 * FuarSalonlariSecici.jsx — Fuar oluştur/düzenle ekranında, seçilen fuar alanının salonları ve bu
 * fuarda aktif/pasif durumları (backlog #192, 2026-09-26). Aktif = salon_to_fuar satırı var (#157).
 *
 * Üç hâl (Bilge'nin kararları, 2026-09-26):
 *   OLUŞTURMA      Fuarın henüz id'si yok: salonlar anahtarlı listelenir (hepsi açık başlar),
 *                  seçim `onSelectionChange(seçilenIdler, tümIdler)` ile forma bildirilir; form
 *                  kaydedince fuarSlice.createFuar yeni fuarId ile seçilenleri bağlar.
 *   DÜZENLEME      Fuar alanı değişmediyse anahtarlar HEMEN yazar (POST/DELETE
 *                  /fuar/{fuarId}/salon-to-fuar/{salonId}, Salonlar sayfasıyla aynı uçlar).
 *   ALAN DEĞİŞTİ   Kaydedilmemiş yeni bir fuar alanı seçildiyse yeni alanın salonları salt okunur
 *                  gösterilir; kaydedince fuarSlice.updateFuar ESKİ alandaki standsız salonların
 *                  bağını kaldırır (standı olanlar reddedilir ve listelenir), iki alanda da kayıtlı
 *                  salonlar kalır. Yeni alanın salonları kayıttan sonra buradan aktif edilir.
 */
import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Badge, Button, Spinner, Alert, Input, Label } from "reactstrap";
import {
  listSalonlar, addSalonToFuar, removeSalonFromFuar, activateAllFairgroundSalons,
} from "../../services/fuarAlaniSalonService";
import { getFuarHMax } from "../../services/fuarKuralService";
import { useTranslation } from "react-i18next";

const sortHalls = (rows) => [...rows].sort((a, b) => {
  const an = Number(a.salonName), bn = Number(b.salonName);
  if (!Number.isNaN(an) && !Number.isNaN(bn)) return an - bn;
  return String(a.salonName).localeCompare(String(b.salonName), "tr");
});

export default function FuarSalonlariSecici({ fuarAlaniId, fuarId, savedFuarAlaniId, onSelectionChange }) {
  const { t: tx } = useTranslation("components");
  const createMode = fuarId == null;
  const alanDegisti = !createMode && String(fuarAlaniId || "") !== String(savedFuarAlaniId || "");

  const [halls, setHalls] = useState([]);
  const [linked, setLinked] = useState(new Set());   // düzenleme: bu fuara bağlı salonId'ler
  const [selected, setSelected] = useState(new Set()); // oluşturma: seçilen salonId'ler
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  const onSelRef = useRef(onSelectionChange);
  useEffect(() => { onSelRef.current = onSelectionChange; });

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    if (!fuarAlaniId) return undefined;
    let ignore = false;
    async function run() {
      setLoading(true);
      setError(null);
      try {
        const [rows, ctx] = await Promise.all([
          listSalonlar(fuarAlaniId),
          !createMode && !alanDegisti ? getFuarHMax(fuarId, { forceLive: true }) : Promise.resolve(null),
        ]);
        if (ignore) return;
        const sorted = sortHalls(rows);
        setHalls(sorted);
        setLinked(new Set((ctx?.halls ?? []).map((h) => String(h.salonId))));
        if (createMode) {
          // Yeni bir fuar alanı seçilince hepsi açık başlar (Bilge, 2026-09-26).
          const all = sorted.map((h) => String(h.salonId));
          setSelected(new Set(all));
          onSelRef.current?.(all, all);
        }
      } catch (e) {
        if (!ignore) setError(e.message);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => { ignore = true; };
  }, [fuarAlaniId, fuarId, createMode, alanDegisti, reloadToken]);

  // Fuar alanı boşaltılırsa (oluşturmada) seçim de boşalır.
  useEffect(() => {
    if (createMode && !fuarAlaniId) onSelRef.current?.([], []);
  }, [createMode, fuarAlaniId]);

  const toggleSelected = (salonId) => {
    const key = String(salonId);
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
    onSelRef.current?.([...next], halls.map((h) => String(h.salonId)));
  };

  async function runAction(fn, okMsg) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      setMsg(okMsg);
      setReloadToken((t) => t + 1);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  if (!fuarAlaniId) {
    return <div className="text-muted small">{tx("fuarSalonlariSecici.salonlariGormekIcinBirFuar")}</div>;
  }

  const inactiveCount = halls.filter((h) => !linked.has(String(h.salonId))).length;

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <span className="fw-semibold small">{tx("fuarSalonlariSecici.salonlar")}</span>
        <Badge color="light" className="text-dark">{halls.length}</Badge>
        {createMode && halls.length > 0 && (
          <span className="text-muted small">{tx("fuarSalonlariSecici.acikOlanlarFuarKaydedilinceBu", { size: selected.size, length: halls.length })}</span>
        )}
        {!createMode && !alanDegisti && inactiveCount > 0 && (
          <Button size="sm" color="primary" outline disabled={busy}
            onClick={() => runAction(() => activateAllFairgroundSalons(fuarId), tx("fuarSalonlariSecici.kayitliTumSalonlarBuFuara"))}>
            {tx("fuarSalonlariSecici.tumunuAktifEt", { inactiveCount })}
          </Button>
        )}
        {fuarAlaniId && (
          <Link to={`/Management/FairAreas/${fuarAlaniId}/Salonlar`} className="small ms-auto">{tx("fuarSalonlariSecici.fuarAlanininSalonlari")}</Link>
        )}
      </div>

      {alanDegisti && (
        <Alert color="warning" className="py-2 small mb-2">
          {tx("fuarSalonlariSecici.fuarAlaniDegistiKaydedinceEski")}{" "}<strong>{tx("fuarSalonlariSecici.standiOlmayan")}</strong>{" "}{tx("fuarSalonlariSecici.salonlarinBuFuarlaBagiKaldirilir")}
        </Alert>
      )}
      {error && <Alert color="danger" className="py-2 small mb-2" toggle={() => setError(null)}>{error}</Alert>}
      {msg && <Alert color="success" className="py-2 small mb-2" toggle={() => setMsg(null)}>{msg}</Alert>}

      {loading ? (
        <div className="py-2"><Spinner size="sm" color="primary" /></div>
      ) : halls.length === 0 ? (
        <div className="text-muted small">{tx("fuarSalonlariSecici.buFuarAlanindaKayitliSalon")}</div>
      ) : (
        <div className="d-flex flex-wrap gap-2">
          {halls.map((h) => {
            const key = String(h.salonId);
            if (createMode) {
              const on = selected.has(key);
              const id = `fuar-salon-sec-${key}`;
              return (
                <div key={key} className={`border rounded px-2 py-1 d-flex align-items-center gap-2 ${on ? "border-primary" : ""}`}>
                  <div className="form-check form-switch mb-0">
                    <Input type="switch" id={id} className="form-check-input" checked={on} onChange={() => toggleSelected(h.salonId)} />
                    <Label className="form-check-label small mb-0" for={id}>{h.salonName}</Label>
                  </div>
                </div>
              );
            }
            if (alanDegisti) {
              return (
                <Badge key={key} color="light" className="text-dark border px-2 py-1">{h.salonName}</Badge>
              );
            }
            const aktif = linked.has(key);
            return (
              <div key={key} className="border rounded px-2 py-1 d-flex align-items-center gap-2">
                <span className="small fw-semibold">{h.salonName}</span>
                <Badge color={aktif ? "success" : "secondary"} pill>{aktif ? tx("fuarSalonlariSecici.aktif") : tx("fuarSalonlariSecici.pasif")}</Badge>
                <Button
                  size="sm" color={aktif ? "danger" : "primary"} outline className="py-0 px-1" disabled={busy}
                  title={aktif ? tx("fuarSalonlariSecici.buFuardanCikarStandiVarsa") : tx("fuarSalonlariSecici.buFuaraBagla")}
                  onClick={() => runAction(
                    () => (aktif ? removeSalonFromFuar(fuarId, h.salonId) : addSalonToFuar(fuarId, h.salonId)),
                    aktif ? tx("fuarSalonlariSecici.buFuardanCikarildi", { salonName: h.salonName }) : tx("fuarSalonlariSecici.buFuaraBaglandi", { salonName: h.salonName }),
                  )}
                >
                  {aktif ? tx("fuarSalonlariSecici.pasifYap") : tx("fuarSalonlariSecici.aktifEt")}
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
